/**
 * Formats a datetime-local input string (yyyy-MM-ddTHH:mm or yyyy-MM-ddTHH:mm:ss)
 * as an ISO-8601 string WITHOUT timezone conversion, preserving the local time
 * exactly as the user entered it (e.g. "2024-06-20T14:00:00").
 *
 * Use this instead of new Date(value).toISOString() to avoid UTC offset.
 */
export function toLocalISOString(value: string | null | undefined): string {
  if (!value) return '';
  const clean = value.trim();
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2})?$/.test(clean)) {
    return clean.length === 16 ? `${clean}:00` : clean;
  }
  // Fallback: parse and format manually to avoid toISOString() UTC shift
  const d = new Date(clean);
  if (Number.isNaN(d.getTime())) return clean;
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}

/**
 * Converts a backend ISO datetime string to the value format expected by
 * <input type="datetime-local"> (yyyy-MM-ddTHH:mm), without timezone conversion.
 *
 * Use this instead of new Date(value) when patching reactive form controls
 * that bind to datetime-local inputs.
 */
export function toInputDateTime(value: string | null | undefined): string | null {
  if (!value) return null;
  // Already looks like a datetime-local value — return trimmed to HH:mm
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(value)) {
    return value.substring(0, 16);
  }
  // Parse without timezone shift: split the string directly
  const match = value.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/);
  if (match) {
    return `${match[1]}-${match[2]}-${match[3]}T${match[4]}:${match[5]}`;
  }
  return null;
}

/**
 * Converts a backend date-only string (yyyy-MM-dd) to a Date object
 * using local time (avoids the UTC midnight → previous day shift).
 */
export function parseLocalDate(value: string | null | undefined): Date | null {
  if (!value) return null;
  const parts = value.split('-');
  if (parts.length !== 3) return null;
  return new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]));
}

/**
 * Returns today's date as a yyyy-MM-dd string using local time.
 * Use this instead of new Date().toISOString().split('T')[0].
 */
export function todayLocalDateString(): string {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/**
 * Returns a date N days from today as a yyyy-MM-dd string using local time.
 */
export function localDateStringPlusDays(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() + days);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/**
 * Returns the current local datetime as an ISO-8601 string WITHOUT timezone conversion.
 * Use this instead of new Date().toISOString() when sending "now" to the backend.
 * e.g. "2024-06-20T14:30:00"
 */
export function nowLocalISOString(): string {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}
